import React, { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useEvents } from '../../../context/EventsContext';
import { cleanTitle } from '../../../utils/text';

/**
 * Carrousel horizontal des événements à venir (extrait de HomeScreen).
 * Autonome : lit useEvents. Ne rend RIEN s'il n'y a aucun événement futur.
 * Markup identique à l'ancien HomeScreen.
 */

const formatEventDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};

export default function EventsSection({ props = {} } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionTitle = props.title || 'Événements';
  const maxEvents = Number.isInteger(props.maxEvents) && props.maxEvents > 0 ? props.maxEvents : 8;
  const { events: dynamicEvents } = useEvents();

  const openEventDetail = useCallback((eventId) => {
    navigate(`/events/${eventId}`, { state: { backgroundLocation: location } });
  }, [navigate, location]);

  const futureEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dynamicEvents.filter((e) => {
      const d = new Date(e.startDate || e.date);
      return !isNaN(d.getTime()) && d >= today;
    });
  }, [dynamicEvents]);

  if (futureEvents.length === 0) return null;

  return (
    <div className="py-6 bg-pcc-surface2">
      <div className="flex items-center justify-between px-4 mb-4">
        <h2 className="text-lg font-bold text-pcc-text">{sectionTitle}</h2>
        <button
          onClick={() => navigate('/events')}
          className="text-sm font-semibold text-[#9398DE] flex items-center gap-1"
        >
          Tout voir <ChevronRight size={16} />
        </button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 px-4 snap-x snap-mandatory">
        {futureEvents.slice(0, maxEvents).map((event) => (
          <button
            key={event.id}
            onClick={() => openEventDetail(event.id)}
            className="flex-shrink-0 w-[280px] snap-start group"
          >
            <div className="rounded-2xl overflow-hidden shadow-md transition-all group-hover:shadow-lg bg-pcc-card">
              <div className="w-[280px] h-[180px] overflow-hidden">
                <img
                  src={(event.imageUrl || event.image || 'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?w=800&q=85').replace(/w=\d+/, 'w=800').replace(/q=\d+/, 'q=85')}
                  alt={cleanTitle(event.title)}
                  className="w-full h-full object-cover object-top transition-transform group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <h3 className="text-sm font-bold truncate text-pcc-text">{cleanTitle(event.title)}</h3>
                <p className="text-xs mt-1 text-pcc-text-muted">{formatEventDate(event.startDate || event.date)}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
